import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/queryKeys'
import type { ClientRow, ClientSavePayload, UpdateFeatureAccessRequest, UploadClientLogoPayload } from './types'
import {
  createClient,
  deleteClient,
  deleteClientLogo,
  fetchClientFeatureAccess,
  fetchClientDetail,
  fetchClientLogo,
  fetchClients,
  fetchPbiGroups,
  type FetchClientsParams,
  updateClient,
  updateClientFeatureAccess,
  uploadClientLogo,
} from './api'

export type ClientStatusFilter = 'all' | 'Active' | 'Inactive'

export function useClientListFilters(allClients: ClientRow[]) {
  const [nameFilter, setNameFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<ClientStatusFilter>('all')

  const clientList = useMemo(() => {
    return allClients.filter((client) => {
      if (statusFilter !== 'all' && (statusFilter === 'Active' ? client.statusId !== 1 : client.statusId !== 2)) {
        return false
      }

      const trimmedName = nameFilter.trim().toLowerCase()
      if (trimmedName && !client.name.toLowerCase().includes(trimmedName)) return false

      return true
    })
  }, [allClients, statusFilter, nameFilter])

  return {
    nameFilter,
    setNameFilter,
    statusFilter,
    setStatusFilter,
    clientList,
  }
}

export function useClients(params: FetchClientsParams = {}) {
  return useQuery({
    queryKey: queryKeys.clients.list(params),
    queryFn: () => fetchClients(params),
  })
}

export function usePbiGroups() {
  return useQuery({
    queryKey: queryKeys.clients.pbiGroups(),
    queryFn: fetchPbiGroups,
  })
}

export function useClientDetail(clientId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.clients.detail(clientId),
    queryFn: () => fetchClientDetail(clientId),
    enabled: enabled && !!clientId,
  })
}

export function useClientLogo(clientId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.clients.logo(clientId),
    queryFn: () => fetchClientLogo(clientId),
    enabled: enabled && !!clientId,
  })
}

export function useClientFeatureAccess(clientId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.clients.featureAccess(clientId),
    queryFn: () => fetchClientFeatureAccess(clientId),
    enabled: enabled && !!clientId,
  })
}

export function useCreateClientMutation(filters: FetchClientsParams = {}) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ClientSavePayload) => createClient(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.list(filters) })
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all })
    },
  })
}

export function useUpdateClientMutation(filters: FetchClientsParams = {}) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ClientSavePayload & { clientId: string }) => updateClient(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.list(filters) })
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.detail(variables.clientId) })
    },
  })
}

export function useDeleteClientMutation(filters: FetchClientsParams = {}) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (clientId: string) => deleteClient(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.list(filters) })
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all })
    },
  })
}

export function useUploadClientLogoMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UploadClientLogoPayload) => uploadClientLogo(payload),
    onSuccess: (_, variables) => {
      queryClient.removeQueries({ queryKey: queryKeys.clients.logo(variables.clientId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.logo(variables.clientId) })
    },
  })
}

export function useDeleteClientLogoMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (clientId: string) => deleteClientLogo(clientId),
    onSuccess: (_, clientId) => {
      queryClient.removeQueries({ queryKey: queryKeys.clients.logo(clientId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.logo(clientId) })
    },
  })
}

export function useUpdateClientFeatureAccessMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateFeatureAccessRequest) => updateClientFeatureAccess(clientId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.clients.featureAccess(clientId), data)
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.featureAccess(clientId) })
    },
  })
}

export type UseUpdateClientMutationResult = ReturnType<typeof useUpdateClientMutation>
export type UseDeleteClientMutationResult = ReturnType<typeof useDeleteClientMutation>
export type UseUploadClientLogoMutationResult = ReturnType<typeof useUploadClientLogoMutation>
